import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components";
import { Dialog } from "@/components/ui/dialog";
import { Check, X, Eye } from "lucide-react";
import { contentService } from "@/features/content/services/content.service";
import type { ContentItem, ContentType } from "@/features/content/interfaces";

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString();
}

function isVideo(type: ContentType): boolean {
  return type === "VIDEO";
}

export const ContentReview: React.FC = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [viewingMedia, setViewingMedia] = useState<ContentItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["content", page, statusFilter],
    queryFn: () =>
      contentService.getContent({
        page,
        limit: 20,
        ...(statusFilter && statusFilter !== "all" && { status: statusFilter as "LIVE" | "DRAFT" | "REVIEW" | "DELETED" }),
      }),
  });

  const items = data?.data ?? [];
  const pagination = data?.pagination;

  const mediaUrl = viewingMedia?.data?.url;
  const mediaType = viewingMedia?.type;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <h1 className="text-3xl font-bold mb-2">Content Review</h1>
      <p className="text-muted-foreground mb-8">
        Review and approve content before it goes live
      </p>

      {/* Optional filter */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-sm text-muted-foreground">Status:</span>
        <select
          value={statusFilter ?? "all"}
          onChange={(e) => {
            setStatusFilter(e.target.value === "all" ? undefined : e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-input rounded-md bg-background text-sm"
        >
          <option value="all">All</option>
          <option value="DRAFT">Draft</option>
          <option value="REVIEW">Review</option>
          <option value="LIVE">Live</option>
          <option value="DELETED">Deleted</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center">
          No content yet. Generate content from a plan in Content Wizard.
        </p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex flex-col sm:flex-row gap-4 p-4 border border-input rounded-lg bg-card hover:border-primary/30 transition-colors"
            >
              <div className="sm:w-48 shrink-0">
                <button
                  type="button"
                  onClick={() => setViewingMedia(item)}
                  className="w-full aspect-video rounded-lg overflow-hidden bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  {item.data?.url ? (
                    isVideo(item.type) ? (
                      <video
                        src={item.data.url}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={item.data.url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                      No preview
                    </div>
                  )}
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 gap-1"
                  onClick={() => setViewingMedia(item)}
                >
                  <Eye className="h-4 w-4" />
                  View media
                </Button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold">
                      {item.data?.caption
                        ? item.data.caption.slice(0, 60) + (item.data.caption.length > 60 ? "…" : "")
                        : `Content #${item.id}`}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {item.data?.caption ?? "No caption"}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 px-2 py-1 rounded text-xs font-medium ${
                      item.status === "LIVE"
                        ? "bg-green-500/10 text-green-600 dark:text-green-400"
                        : item.status === "DELETED"
                          ? "bg-destructive/10 text-destructive"
                          : item.status === "REVIEW"
                            ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                            : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {item.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-4 text-sm text-muted-foreground">
                  {item.data?.brandName && (
                    <span>Brand: {item.data.brandName}</span>
                  )}
                  <span>{item.platform}</span>
                  <span>{item.type}</span>
                  <span>{formatRelativeTime(item.createdAt)}</span>
                </div>
                {item.status === "DRAFT" && (
                  <div className="flex gap-2 mt-4">
                    <Button size="sm">
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button size="sm" variant="outline">
                      <X className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= pagination.totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Media view modal */}
      <Dialog
        open={viewingMedia != null}
        onOpenChange={(open) => !open && setViewingMedia(null)}
        title={viewingMedia ? `Content #${viewingMedia.id}` : undefined}
        description={viewingMedia?.data?.caption}
        size="full"
      >
        {viewingMedia && mediaUrl && (
          <div className="flex flex-col items-center gap-4">
            {mediaType && isVideo(mediaType) ? (
              <video
                src={mediaUrl}
                controls
                autoPlay
                className="max-w-full max-h-[70vh] rounded-lg bg-black"
              />
            ) : (
              <img
                src={mediaUrl}
                alt=""
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            )}
            {viewingMedia.data?.caption && (
              <p className="text-sm text-muted-foreground text-center max-w-2xl">
                {viewingMedia.data.caption}
              </p>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};
